import {
  FileSpreadsheet,
  FileText,
  Presentation,
  type LucideIcon,
} from "lucide-react";

import type { ReportFormat } from "@/types/api";

/**
 * Visual identity for a report file format: short uppercase label, lucide
 * icon, and the icon tint used on the thumbnail-corner chip.
 *
 * PDF is deliberately neutral (slate), not the conventional red — red is
 * already the FAILED-row accent, and PDF is the overwhelming default, so it
 * should recede. The colour is spent on the rarer formats (an amber deck, a
 * green tracker) which are the ones worth catching the eye when scanning.
 */
export interface FormatMeta {
  label: string;
  Icon: LucideIcon;
  iconClass: string;
}

const FORMAT_META: Record<string, FormatMeta> = {
  pdf: { label: "PDF", Icon: FileText, iconClass: "text-slate-300" },
  pptx: { label: "PPTX", Icon: Presentation, iconClass: "text-amber-400" },
  docx: { label: "DOCX", Icon: FileText, iconClass: "text-sky-400" },
  xlsx: { label: "XLSX", Icon: FileSpreadsheet, iconClass: "text-emerald-400" },
};

/**
 * Resolve presentation metadata for a format, failing safe for any value the
 * frontend type doesn't know yet (e.g. a new backend format shipped before
 * `ReportFormat` is widened): uppercase the raw string, neutral tint, generic
 * document icon.
 */
export function metaForFormat(format: string): FormatMeta {
  return (
    FORMAT_META[format] ?? {
      label: format.toUpperCase(),
      Icon: FileText,
      iconClass: "text-slate-300",
    }
  );
}

/** Human-readable format label (e.g. "PPTX"), for use in accessible names. */
export function formatLabel(format: ReportFormat | string): string {
  return metaForFormat(format).label;
}
